import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaUser, FaCalendarAlt, FaTag, FaClock, FaCalendar, FaComments, FaShareAlt } from 'react-icons/fa';
import axios from 'axios';
import { endpoints } from '../config/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './BlogPost.css';
import ShareButtons from '../components/common/ShareButtons';
import CustomImage from '../components/common/CustomImage';
import SimpleBlogCard from '../components/blog/SimpleBlogCard';
import BlogPostSchema from '../components/seo/BlogPostSchema';
import { updatePageSEO, debugSEO } from '../utils/seoUtils';
import { getBestOpenGraphImage } from '../utils/imageUtils';
import RecentBlogs from '../components/home/RecentBlogs';
import RecentJobs from '../components/home/RecentJobs';
import { AdUnitBeforePost, AdUnitPost } from '../components/adsense';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date available';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  useEffect(() => {
    const fetchBlogPost = async () => {
      if (!slug) {
        setError('Blog slug is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
                // Use the new slug-based endpoint from backend
        const response = await axios.get(`${endpoints.blogs.list}/slug/${slug}`);
        
        if (response.data.success) {
          const currentBlog = response.data.data;
          const seoDataFromAPI = response.data.seo;
          
          // Process the blog data
          const processedBlog = {
            ...currentBlog,
            publicationDate: currentBlog.publicationDate || currentBlog.createdAt || currentBlog.date,
            slug: currentBlog.slug,
            // Handle heroImage object structure
            heroImage: typeof currentBlog.heroImage === 'object' && currentBlog.heroImage?.url 
              ? currentBlog.heroImage.url 
              : currentBlog.heroImage || '/images/blog-placeholder.jpg',
            // Handle tags
            tags: Array.isArray(currentBlog.tags) ? currentBlog.tags : [],
            // Handle category
            category: currentBlog.category || 'General',
            // Handle excerpt
            excerpt: currentBlog.excerpt || currentBlog.description || currentBlog.summary || '',
            // Handle comments count
            commentsCount: currentBlog.commentsCount || 0,
            // Handle content
            content: currentBlog.content || currentBlog.body || currentBlog.description || '',
            // Handle description
            description: currentBlog.description || currentBlog.excerpt || currentBlog.summary || ''
          };

          setBlog(processedBlog);
          setSeoData(seoDataFromAPI);
          
          // Update SEO meta tags directly with blog data for image fallback
          if (seoDataFromAPI) {
            // Pass processedBlog as contentData to extract images if SEO data lacks them
            updatePageSEO(seoDataFromAPI, processedBlog);
            
            // Debug in development
            if (process.env.NODE_ENV === 'development') {
              debugSEO();
            }
          } else {
            // If no SEO data from API, create basic SEO from blog data
            const baseUrl = 'https://jobsopportunity.in';
            const blogUrl = `${baseUrl}/blog/${processedBlog.slug}`;
            const blogImage = getBestOpenGraphImage(processedBlog, 'blog');
            
            const fallbackSEO = {
              title: `${processedBlog.title} - Jobs Opportunity`,
              description: processedBlog.excerpt || processedBlog.description || 'Read our latest blog post',
              canonical: blogUrl,
              og: {
                title: `${processedBlog.title} - Jobs Opportunity`,
                description: processedBlog.excerpt || processedBlog.description || 'Read our latest blog post',
                url: blogUrl,
                type: 'article',
                site_name: 'Jobs Opportunity',
                image: blogImage,
                image_width: '1200',
                image_height: '630',
                published_time: processedBlog.publicationDate,
                modified_time: processedBlog.updatedAt || processedBlog.publicationDate,
                author: processedBlog.authorName
              },
              twitter: {
                card: 'summary_large_image',
                title: `${processedBlog.title} - Jobs Opportunity`,
                description: processedBlog.excerpt || processedBlog.description || 'Read our latest blog post',
                image: blogImage
              }
            };
            updatePageSEO(fallbackSEO, processedBlog);
          }
        } else {
          setError('Blog post not found');
          return;
        }

        // Note: Related posts functionality temporarily disabled
        // as we're using direct slug endpoints instead of fetching all blogs
        setRelatedPosts([]);

      } catch (error) {
        console.error('Error fetching blog post:', error);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="error-container">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={() => navigate('/blogs')}>Back to Blogs</button>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="error-container">
        <h3>Blog post not found</h3>
        <button onClick={() => navigate('/blogs')}>Back to Blogs</button>
      </div>
    );
  }

  return (
    <div className="blog-post-container">
      {/* SEO Components */}
      {blog && (
        <BlogPostSchema blog={blog} />
      )}

      {/* Title and Meta Section */}
      <header className="blog-header">
        <h1 className="blog-title">{blog.title}</h1>
        <div className="blog-meta-info">
          <span className="author-name">{blog.authorName}</span>
          <span className="post-date">{formatDate(blog.publicationDate)}</span>
          <span className="post-category">{blog.category}</span>
          <span className="comment-count">{blog.commentsCount || 0} Comments</span>
        </div>
      </header>

      {/* Updated Hero Image Section */}
      <div className="blog-hero-image">
        <CustomImage
          src={blog?.heroImage}
          alt={blog?.title}
          className="hero-image"
          fallbackSrc="/images/blog-placeholder.jpg"
        />
      </div>

      {/* Ad Before Post Content */}
      <AdUnitBeforePost />

      {/* Main Content */}
      <div className="blog-content-wrapper">
        {/* Left Column - Main Content and Share */}
        <div className="blog-left-column">
          <article className="blog-content">
            <div className="blog-description">
              {blog.excerpt}
            </div>
            <div className="blog-content-container">
              <div 
                className="blog-body rich-text-content"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />
            </div>
          </article>

          {/* Ad After Post Content */}
          <AdUnitPost />

          {/* Share and Related Section */}
          <section className="share-and-related">
            <div className="share-section">
              <ShareButtons 
                title={blog.title}
                description={blog.excerpt}
                url={window.location.href}
                type="blog"
              />
            </div>

            {relatedPosts.length > 0 && (
              <div className="related-section">
                <h3>Related Posts</h3>
                <div className="related-posts-grid">
                  {relatedPosts.map(post => (
                    <Link 
                      key={post._id} 
                      to={`/blog/${post.slug}`}
                      className="related-post"
                    >
                      <div className="related-post-image-container">
                        <CustomImage
                          src={post.heroImage}
                          alt={post.title}
                          className="related-post-image"
                          fallbackSrc="/images/blog-placeholder.jpg"
                        />
                      </div>
                      <div className="related-post-info">
                        <h4>{post.title}</h4>
                        <span className="date">
                          {formatDate(post.publicationDate)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Right Column - Sidebar Content */}
        <div className="blog-right-content">
          {/* Recent Blogs Section */}
          <div className="right-section">
            <h3 className="right-section-title">Recent Blog Posts</h3>
            <RecentBlogs currentBlog={blog} limit={4} variant="sidebar" />
          </div>

          {/* Recent Jobs Section */}
          <div className="right-section">
            <h3 className="right-section-title">Latest Job Opportunities</h3>
            <RecentJobs limit={4} variant="sidebar" />
          </div>

          {/* Quick Navigation */}
          <div className="right-section">
            <h3 className="right-section-title">Quick Navigation</h3>
            <div className="quick-nav">
              <Link to="/blogs" className="quick-nav-item">
                <span className="quick-nav-icon">📝</span>
                <span className="quick-nav-text">All Blog Posts</span>
              </Link>
              <Link to="/search" className="quick-nav-item">
                <span className="quick-nav-icon">🔍</span>
                <span className="quick-nav-text">Search Jobs</span>
              </Link>
              <Link to="/" className="quick-nav-item">
                <span className="quick-nav-icon">🏠</span>
                <span className="quick-nav-text">Home</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Related Blogs Section */}
      <RecentBlogs currentBlog={blog} limit={3} />
    </div>
  );
};

export default BlogPost;
